// Which squares of the coverage grid lie along the trail shown on the map.
// The grid holds positions from every race. A race shows only the squares
// along its own trail: the route file of the class, or the tracks of the
// contestants when the class has no route file.
// A line is a list of [lat, lng]. Every function leaves its input alone.

import { clampBox, snapBox } from './coverageBounds';

// The grid of the server, see backend/lib/gps-coverage.js.
export const CELL_WIDTH = 360 / 32768;
export const CELL_HEIGHT = 180 / 32768;

const DEFAULT_MARGIN = 1;
// How far, in cells, a cell without colour looks for a coloured square.
const FILL_REACH = 2;
// The colour a cell takes when squares of both colours are equally near.
const STATUS_BAD = 'bad';
// Columns and rows are below 32768, also with a margin added, so a column and
// a row fit in one number without two cells sharing it.
const KEY_FACTOR = 65536;
const LINE_TYPES = ['LineString', 'MultiLineString'];

function column(lng) {
    return Math.floor((lng + 180) / CELL_WIDTH);
}

function row(lat) {
    return Math.floor((lat + 90) / CELL_HEIGHT);
}

function keyOf(cx, cy) {
    return (cx * KEY_FACTOR) + cy;
}

export function cellKey(lat, lng) {
    return keyOf(column(lng), row(lat));
}

function isPosition(lat, lng) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return false;
    }
    // A tracker without a fix reports 0, 0.
    return !(lat === 0 && lng === 0);
}

function toLine(coordinates) {
    return coordinates
        .filter(coordinate => isPosition(coordinate[1], coordinate[0]))
        .map(coordinate => [coordinate[1], coordinate[0]]);
}

function hasRoute(file) {
    const features = file?.geoJSON?.features || [];
    return features.some(feature => LINE_TYPES.includes(feature?.geometry?.type));
}

// GeoJSON has longitude first. Checkpoints (points) and areas are left out.
export function routeLines(files) {
    const lines = [];
    (files || []).forEach((file) => {
        (file?.geoJSON?.features || []).forEach((feature) => {
            const { type, coordinates } = feature?.geometry || {};
            if (type === 'LineString') {
                lines.push(toLine(coordinates || []));
            } else if (type === 'MultiLineString') {
                (coordinates || []).forEach(part => lines.push(toLine(part || [])));
            }
        });
    });
    return lines.filter(line => line.length > 0);
}

function contestantTrackers(trackers) {
    return (trackers || []).filter(tracker => tracker?.contestant?.id
        && tracker.points && tracker.points.length > 0);
}

// The map draws tracks only for trackers tied to a contestant. A position is
// [lat, lng, timestamp, ...].
export function trackLines(trackers) {
    return contestantTrackers(trackers)
        .map(tracker => tracker.points
            .filter(point => isPosition(point[0], point[1]))
            .map(point => [point[0], point[1]]))
        .filter(line => line.length > 0);
}

// Cheap to work out, so the map can ask every second whether the trail has
// changed without walking through the trail itself.
export function trailSignature(files, trackers) {
    const routes = (files || []).filter(file => hasRoute(file));
    if (routes.length > 0) {
        return `route:${routes.map(file => `${file.md5}/${file.geoJSON.features.length}`).join(',')}`;
    }
    const tracks = contestantTrackers(trackers);
    const positions = tracks.reduce((sum, tracker) => sum + tracker.points.length, 0);
    return `tracks:${tracks.length}:${positions}`;
}

// True when both signatures are tracks of the same number of trackers, so the
// only news is positions that have come in. Another number of trackers means
// another class is shown.
export function isSameTracksGrown(previous, next) {
    const [previousKind, previousCount] = `${previous || ''}`.split(':');
    const [nextKind, nextCount] = `${next || ''}`.split(':');
    return previousKind === 'tracks' && nextKind === 'tracks' && previousCount === nextCount;
}

export function buildTrail(files, trackers, margin = DEFAULT_MARGIN) {
    const route = routeLines(files);
    const lines = route.length > 0 ? route : trackLines(trackers);

    const cells = new Set();
    // The cells the trail itself passes through, without the margin.
    const onRoute = new Set();
    const reach = {
        west: Infinity, east: -Infinity, south: Infinity, north: -Infinity,
    };
    const add = (lat, lng) => {
        const cx = column(lng);
        const cy = row(lat);
        onRoute.add(keyOf(cx, cy));
        reach.west = Math.min(reach.west, cx);
        reach.east = Math.max(reach.east, cx);
        reach.south = Math.min(reach.south, cy);
        reach.north = Math.max(reach.north, cy);
        for (let dx = -margin; dx <= margin; dx += 1) {
            for (let dy = -margin; dy <= margin; dy += 1) {
                cells.add(keyOf(cx + dx, cy + dy));
            }
        }
    };

    lines.forEach((line) => {
        line.forEach(([lat, lng], index) => {
            if (index > 0) {
                // Points of a route can lie kilometres apart. Walk between
                // them in steps of half a cell, so no cell is stepped over.
                const [fromLat, fromLng] = line[index - 1];
                const steps = Math.ceil(2 * Math.max(
                    Math.abs(lng - fromLng) / CELL_WIDTH,
                    Math.abs(lat - fromLat) / CELL_HEIGHT,
                ));
                for (let step = 1; step < steps; step += 1) {
                    add(
                        fromLat + (((lat - fromLat) * step) / steps),
                        fromLng + (((lng - fromLng) * step) / steps),
                    );
                }
            }
            add(lat, lng);
        });
    });

    if (cells.size === 0) {
        return { cells, route: onRoute, box: null };
    }
    // Measured from the edges of the cells, so the area holds every square
    // that can be drawn.
    const box = clampBox(snapBox({
        south: ((reach.south - margin) * CELL_HEIGHT) - 90,
        west: ((reach.west - margin) * CELL_WIDTH) - 180,
        north: ((reach.north + margin + 1) * CELL_HEIGHT) - 90,
        east: ((reach.east + margin + 1) * CELL_WIDTH) - 180,
    }));
    return { cells, route: onRoute, box };
}

// A cell is [south, west, north, east, ...] as the server sends it.
export function isOnTrail(cell, cells) {
    return cells.has(cellKey((cell[0] + cell[2]) / 2, (cell[1] + cell[3]) / 2));
}

// The status of the coloured squares nearest to a cell, looking at most
// `reach` cells away in every direction.
function nearestStatus(coloured, cx, cy, reach) {
    for (let distance = 1; distance <= reach; distance += 1) {
        const found = [];
        for (let dx = -distance; dx <= distance; dx += 1) {
            for (let dy = -distance; dy <= distance; dy += 1) {
                const onRing = Math.max(Math.abs(dx), Math.abs(dy)) === distance;
                const status = onRing ? coloured.get(keyOf(cx + dx, cy + dy)) : undefined;
                if (status) {
                    found.push(status);
                }
            }
        }
        if (found.length > 0) {
            return found.includes(STATUS_BAD) ? STATUS_BAD : found[0];
        }
    }
    return null;
}

// A cell along the route without colour breaks the band of squares in two.
// Such a cell takes the colour of the nearest coloured square, when one lies
// within `reach` cells. With green and red equally near it becomes red.
// cells: the coloured squares that are drawn. route: the cells the trail
// passes through, see buildTrail. Gives back the new squares only. A filled
// square is an estimate, so it never gives its colour to another.
export function fillHoles(cells, route, reach = FILL_REACH) {
    const coloured = new Map();
    (cells || []).forEach((cell) => {
        coloured.set(cellKey((cell[0] + cell[2]) / 2, (cell[1] + cell[3]) / 2), cell[4]);
    });
    const filled = [];
    if (coloured.size === 0 || !route) {
        return filled;
    }
    route.forEach((key) => {
        if (coloured.has(key)) {
            return;
        }
        const cx = Math.floor(key / KEY_FACTOR);
        const cy = key % KEY_FACTOR;
        const status = nearestStatus(coloured, cx, cy, reach);
        if (!status) {
            return;
        }
        // The same sums as the server does, so the square fits the grid.
        const south = (cy * CELL_HEIGHT) - 90;
        const west = (cx * CELL_WIDTH) - 180;
        filled.push([south, west, south + CELL_HEIGHT, west + CELL_WIDTH, status]);
    });
    return filled;
}
